/**
 * Company settings for CA / audit report exports (config-driven, multi-tenant ready).
 */

import { supabase } from '../lib/supabase';
import { fetchOrgCompanySettingsRow } from '../saas/lib/orgCompanySettings';
import { getPlatformDefaultBranding } from './companyBranding';

export interface CompanyExportInfo {
  companyName: string;
  gstin: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  email: string;
  currency?: string;
}

export function formatCompanyAddress(company: CompanyExportInfo): string {
  const parts = [company.address, company.city, company.state, company.pincode].filter(Boolean);
  return parts.join(', ');
}

export type ExcelExportCompanyHeader = {
  name: string;
  address: string;
  gstin: string;
  pan: string;
  financialYear: string;
  currency: string;
};

export function companyExportInfoToExcelHeader(company: CompanyExportInfo): ExcelExportCompanyHeader {
  const fyEnd = (new Date().getFullYear() + 1).toString().slice(-2);
  return {
    name: company.companyName,
    address: formatCompanyAddress(company),
    gstin: company.gstin || 'Not Provided',
    pan: 'Not Provided',
    financialYear: `FY ${new Date().getFullYear()}-${fyEnd}`,
    currency: company.currency || 'INR',
  };
}

export async function fetchExcelExportCompanyHeader(
  organizationId?: string | null,
): Promise<ExcelExportCompanyHeader> {
  return companyExportInfoToExcelHeader(await fetchCompanyInfoForExport(organizationId));
}

export async function fetchOrgCustomDomain(organizationId?: string | null): Promise<string | null> {
  return (await fetchOrgPublicHostContext(organizationId)).customDomain;
}

export async function fetchOrgPublicHostContext(
  organizationId?: string | null,
): Promise<{ customDomain: string | null; slug: string | null }> {
  if (!organizationId?.trim()) {
    return { customDomain: null, slug: null };
  }
  const { data } = await supabase
    .from('organizations')
    .select('custom_domain, slug')
    .eq('id', organizationId.trim())
    .maybeSingle();
  return {
    customDomain: data?.custom_domain?.trim() || null,
    slug: data?.slug?.trim() || null,
  };
}

export async function fetchCompanyInfoForExport(organizationId?: string | null): Promise<CompanyExportInfo> {
  const data = await fetchOrgCompanySettingsRow<{
    company_name?: string | null;
    gst_number?: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state?: string | null;
    pincode?: string | null;
    phone?: string | null;
    email?: string | null;
    currency?: string | null;
  }>(
    organizationId,
    'company_name, gst_number, address_line1, address_line2, city, state, pincode, phone, email, currency, organization_id',
  );
  const defaults = getPlatformDefaultBranding();

  const addressParts = [data?.address_line1, data?.address_line2].filter(Boolean);
  return {
    companyName: data?.company_name || defaults.companyName,
    gstin: data?.gst_number || '',
    address: addressParts.join(', ') || defaults.address || '',
    city: data?.city || '',
    state: data?.state || '',
    pincode: data?.pincode || '',
    phone: data?.phone || defaults.phone || '',
    email: data?.email || defaults.email || '',
    currency: data?.currency || 'INR',
  };
}
